import React, { ReactNode } from "react";
import { CustomizableComponent, SxProperties } from "../CustomComponents";

export const USER_FILLABLE = Symbol("User fillable");

export type DesignSuggestionValue =
  | typeof USER_FILLABLE
  | string
  | {
      description: string;
      value: typeof USER_FILLABLE;
      defaultValue?: any;
      required?: true;
    };

export type DesignSuggestion = {
  /**
   * Name of a JS-Class group: JS-{GROUPNAME}-ComponentName
   */
  groupName: string | string[];
  /**
   * User-readable name for the design suggestion
   */
  suggestionName: string;
  /**
   * User-readable description for the design suggestion
   */
  description: string | ReactNode;
  for: "QR" | "KIOSK";
  /**
   * A list of classes with preset values
   * When you want a value to be configurable by the user
   * use the symbol USER_FILLABLE as its value
   * @example
   * [
   *  {
   *    className: ".JS-ArticleContent-Paper",
   *    values: {
   *      boxShadow: "none",
   *      backgroundColor: USER_FILLABLE,
   *    },
   *  },
   * ]
   */
  styles: {
    className: CustomizableComponent["className"];
    values: {
      [cssProp in SxProperties]?: DesignSuggestionValue;
    };
  }[];
};

/**
 * Component-based styling
 * When a component (group) is selected, these suggestions will be shown to the user.
 * A suggestion is a collection of related properties of various JS-ClassNames
 */
export const allDesignSuggestions: DesignSuggestion[] = [
  {
    for: "KIOSK",
    groupName: "OrderArticleContent",
    suggestionName: "Disable images",
    description: (
      <>
        Hide the product image on the order page. Note that the image remains visible in the live editor, but not in the
        real application.
      </>
    ),
    styles: [
      {
        className: ".JS-OrderArticleContent-ImageWrapper",
        values: {
          display: "none",
        },
      },
      {
        className: ".JS-OrderArticleContent-Root",
        values: {
          gridTemplateColumns: "0 minmax(0, 3fr) 1fr",
        },
      },
    ],
  },
  {
    for: "QR",
    groupName: ["OrderArticleContent", "OrderPage"],
    suggestionName: "Disable images",
    description: <>Hide the product image on the order page</>,
    styles: [
      {
        className: ".JS-OrderPage-ArticleCard",
        values: {
          gridTemplateColumns: "0 1fr 1fr",
        },
      },
      {
        className: ".JS-OrderArticleContent-ImageWrapper",
        values: {
          display: "none",
        },
      },
    ],
  },
  {
    for: "QR",
    groupName: "ArticlegroupContent",
    suggestionName: "Menu name on banner",
    description: <>Displays the menu name centered on the banner.</>,
    styles: [
      {
        className: ".JS-ArticlegroupContent-Name",
        values: {
          color: {
            description: "The menu title color",
            value: USER_FILLABLE,
            required: true,
          },
          textShadow: "0 0 4px black",
          width: "100vw",
        },
      },
      {
        className: ".JS-ArticlegroupContent-ImageAndNameWrapper",
        values: {
          position: "relative",
          minHeight: "4em",
        },
      },
      {
        className: ".JS-ArticlegroupContent-Name-Container",
        values: {
          position: "absolute",
          left: "50%",
          top: "50%",
          transform: "translate(-50%, -50%)",
          textAlign: "center",
          width: "80vw",
        },
      },
    ],
  },
  // { // position absolute gives trouble ask Jhorie
  //   for: "KIOSK",
  //   groupName: "ArticlegroupContent",
  //   suggestionName: "Menu name on banner",
  //   description: "Displays the menu name centered on the banner",
  //   styles: [
  //     {
  //       className: ".JS-ArticlegroupContent-Banner-Wrapper",
  //       values: {
  //         position: "relative",
  //       },
  //     },
  //     {
  //       className: ".JS-ArticlegroupContent-Text-Collapse",
  //       values: {
  //         position: "absolute",
  //         left: "50%",
  //         top: "50%",
  //         transform: "translate(-50%, -50%)",
  //       },
  //     },
  //     {
  //       className: ".JS-ArticlegroupContent-Name",
  //       values: {
  //         color: {
  //           description: "The menu title color",
  //           value: USER_FILLABLE,
  //           required: true,
  //         },
  //         textShadow: "0 0 4px black",
  //       },
  //     },
  //   ],
  // },
  {
    for: "KIOSK",
    groupName: "ArticlegroupContent",
    suggestionName: "Accented menu",
    description: "Style a menu to stand out with a background color",
    styles: [
      {
        className: ".JS-ArticlegroupContent-Name",
        values: {
          width: "100vw",
          textAlign: "center",
          backgroundColor: {
            value: USER_FILLABLE,
            description: "Accented banner color",
            defaultValue: "#00ff00",
          },
          padding: {
            value: USER_FILLABLE,
            description: "Accented banner size",
          },
          fontFamily: {
            value: USER_FILLABLE,
            description: "Menu title font family",
          },
          color: {
            value: USER_FILLABLE,
            description: "Menu title color",
          },
          fontSize: {
            value: USER_FILLABLE,
            description: "Menu title font size",
          },
        },
      },
    ],
  },
  {
    for: "QR",
    groupName: "ArticlegroupContent",
    suggestionName: "Accented menu",
    description: "Style a menu to stand out with a background color",
    styles: [
      {
        className: ".JS-ArticlegroupContent-HeaderWrapper",
        values: {
          marginLeft: "-8px",
          paddingLeft: "0",
          paddingRight: "0",
          paddingTop: "8px",
          paddingBottom: "8px",
        },
      },
      {
        className: ".JS-ArticlegroupContent-Name-Container",
        values: {
          backgroundColor: {
            value: USER_FILLABLE,
            description: "Accented banner color",
            defaultValue: "#00ff00",
          },
          width: "100vw",
          padding: {
            value: USER_FILLABLE,
            description: "Accented banner size",
          },
        },
      },
      {
        className: ".JS-ArticlegroupContent-Name",
        values: {
          fontFamily: {
            value: USER_FILLABLE,
            description: "Menu title font family",
          },
          color: {
            value: USER_FILLABLE,
            description: "Menu title color",
          },
          fontSize: {
            value: USER_FILLABLE,
            description: "Menu title font size",
          },
          border: USER_FILLABLE,
        },
      },
    ],
  },
  {
    for: "KIOSK",
    groupName: "ArticleContent",
    suggestionName: "Centered",
    description: "Center everything (title, image, description, allergy tags, price) within a product",
    styles: [
      {
        className: ".JS-ArticleContent-Name",
        values: {
          alignSelf: "center",
          textAlign: "center",
        },
      },
      {
        className: ".JS-ArticleContent-Description",
        values: {
          textAlign: "center",
          paddingRight: "0",
        },
      },
      {
        className: ".JS-ArticleContent-PriceWrapper",
        values: {
          alignSelf: "center",
        },
      },
      {
        className: ".JS-ArticleContent-MenuTags",
        values: {
          alignSelf: "center",
        },
      },
    ],
  },
  {
    for: "KIOSK",
    groupName: "ArticleContent",
    suggestionName: "Highlighted",
    description: "Highlight a product with a color of your choosing",
    styles: [
      {
        className: ".JS-ArticleContent-Paper",
        values: {
          boxShadow: "none",
          backgroundColor: USER_FILLABLE,
          borderRadius: "0",
        },
      },
    ],
  },
  {
    for: "QR",
    groupName: "ArticleContent",
    suggestionName: "Banner",
    description: "Style a product to be used as a banner in your menu.",
    styles: [
      {
        className: ".JS-ArticleContent-ButtonBase",
        values: {
          backgroundImage: USER_FILLABLE,
          backgroundRepeat: "no-repeat",
          backgroundSize: "contain",
          pointerEvents: "none",
          minHeight: "140px",
          backgroundPosition: "center",
        },
      },
      {
        className: ".JS-ArticleContent-Description",
        values: {
          display: "none",
        },
      },
      {
        className: ".JS-ArticleContent-Price",
        values: {
          display: "none",
        },
      },
      {
        className: ".JS-ArticleContent-QuickAddOrderButton",
        values: {
          display: "none",
        },
      },
      {
        className: ".JS-ArticleContent-ImageWrapper",
        values: {
          display: "none",
        },
      },
      {
        className: ".JS-ArticleContent-MenuTags",
        values: {
          display: "none",
        },
      },
      {
        className: ".JS-ArticleContent-Name",
        values: {
          display: "none",
        },
      },
      {
        className: ".JS-ArticleContent-Paper",
        values: {
          boxShadow: "none",
          background: "none",
        },
      },
    ],
  },
];
